import React, {useCallback, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {observer} from 'mobx-react-lite';
import {useService} from '../../../../infrastructure/service/service';
import {UploadComponentProps} from '../../../../entity/props/document/upload-component-props';
import {Section} from '../../../../entity/resource/section';
import Box from '@mui/material/Box';
import {LinearProgress, Typography} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {Document} from '../../../../entity/resource/document';
import {DocumentDto, TitleTemplateDto} from '../../../../entity/dto/title-template-dto';

import '../../../../assets/css/widget/upload-component.css';

const UploadComponent = (props: UploadComponentProps): JSX.Element => {
  const {userService, documentService, titleService, sectionService, titleTemplateService, projectService} = useService();

  const [isPermitted, setPermitted] = useState<boolean>(false);
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [documentIndex, setDocumentIndex] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);

  useEffect(() => {
    const title = titleService.getTitle();
    setPermitted(title ? projectService.isPermittedTitle(title.id) : true);
  }, [userService, props.project, titleTemplateService.getTitleTemplate()])

  const onDrop = useCallback(async (acceptedFiles: any): Promise<void> => {
    setUploading(true);
    setDocumentCount(acceptedFiles.length);
    setProgress(0);

    const batchSize = 3;
    const batches = [];

    for (let i = 0; i < acceptedFiles.length; i += batchSize)
      batches.push(acceptedFiles.slice(i, i + batchSize));

    for (const [batchIndex, batch] of batches.entries()) {
      const templateDocuments: DocumentDto[] = [];
      let batchProgressMap = new Map();

      const setBatchProgress = (fileIndex: number, progress: number): void => {
        batchProgressMap.set(fileIndex, progress);

        const totalBatchProgress = [...batchProgressMap.values()].reduce((sum: number, p: number) => sum + p, 0) / batch.length;
        setProgress(Math.min(99, Math.round(totalBatchProgress)));
      };

      await Promise.all(
        batch.map(async (file: any, index: number) => {
          const globalIndex = batchIndex * batchSize + index + 1;
          setDocumentIndex(globalIndex);

          if (props.project) {
            await documentService.uploadFile(
              props.project.createdByOrganizationID,
              file,
              props.id,
              undefined,
              (progress: number) => setBatchProgress(globalIndex, progress));
          } else if (titleTemplateService.getTitleTemplate()) {
            const response = await documentService.uploadFile(
              titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '',
              file,
              undefined,
              undefined,
              (progress: number) => setBatchProgress(globalIndex, progress),
              titleTemplateService.getTitleTemplate()?.id ?? '');

            if (response)
              templateDocuments.push(response);
          }
        })
      );

      let items = {};
      if (props.project) {
        const title = await titleService.getTitleById(props.project?.createdByOrganizationID ?? '', props.id ?? '');
        items = title?.sections?.reduce((acc: any, section: Section) => {
          acc[section.id] = section.documents.map((doc) => doc.id);
          return acc;
        }, {});
      } else if (titleTemplateService.getTitleTemplate()) {
        const sections = titleTemplateService.getTitleTemplate()?.sections.map((section: Section) => ({
          id: section.id,
          name: section.name,
          documents: [
            ...section.documents.map((document: Document) => ({
              id: document.id,
              name: document.name,
              url: document.url
            })),
            ...(section.name === 'Unclassified'
              ? templateDocuments.map((documentDto: DocumentDto) => ({
                id: '',
                name: documentDto.name,
                url: documentDto.url
              }))
              : [])
          ]
        }));

        const hasUnclassified = sections?.some(section => section.name === 'Unclassified');
        if (!hasUnclassified) {
          sections?.push({
            id: '',
            name: 'Unclassified',
            documents: templateDocuments.map((documentDto: DocumentDto) => ({
              id: '',
              name: documentDto.name,
              url: documentDto.url
            }))
          });
        }

        const templatePayload: TitleTemplateDto = {
          name: titleTemplateService.getTitleTemplate()?.name ?? '',
          titleID: titleTemplateService.getTitleTemplate()?.titleID ?? '',
          address: titleTemplateService.getTitleTemplate()?.address ?? '',
          stateID: titleTemplateService.getTitleTemplate()?.stateID ?? '',
          countyID: titleTemplateService.getTitleTemplate()?.countyID ?? '',
          subdivision: titleTemplateService.getTitleTemplate()?.subdivision ?? '',
          sectionIDs: [],
          documentIDs: [],
          sections: sections ?? [],
          tmsParcelNumbers: titleTemplateService.getTitleTemplate()?.tmsParcelNumbers ?? [],
          isPublic: titleTemplateService.getTitleTemplate()?.isPublic ?? true,
          isProjectTitle: true
        };

        await titleTemplateService.updateTitleTemplate(titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '', titleTemplateService.getTitleTemplate()?.id ?? '', templatePayload);

        const updatedTitleTemplate = await titleTemplateService.getTitleTemplateById(titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '', titleTemplateService.getTitleTemplate()?.id ?? '', true);
        items = updatedTitleTemplate?.sections.reduce((acc: any, section: Section) => {
          acc[section.id] = section.documents.map((doc) => doc.id);
          return acc;
        }, {});
      }

      props.setItems(items);
      if (items && Object.keys(items).length)
        sectionService.setMovableItems(items);

      setProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setProgress(0);
    }

    props.setFiles(acceptedFiles);
    setUploading(false);
  }, [])

  const { getRootProps, getInputProps } = useDropzone({onDrop, useFsAccessApi: false, noClick: !isPermitted, noDrag: !isPermitted});

  return (
    <Box id={'upload-container'} className={`upload-container ${!isPermitted ? 'disable' : ''}`} {...getRootProps()}>
      {
        !uploading
          ? <Box id={'upload-zone'} className={'upload-zone'}>
              <input id={'input-upload-files'} hidden disabled={!isPermitted} {...getInputProps()} />
              <Typography id={'text-upload-instructions'} className={'upload-text'}>
                Click to browse and upload or drag and drop your files
              </Typography>
            </Box>
          : <Box id={'progress-zone'} className={'progress-zone'}>
              <Box id={'progress-icon-zone'} className={'progress-icon-zone'}>
                <InsertDriveFileIcon id={'icon-upload-progress'} className={'progress-icon'} />
              </Box>
              <Box id={'progress-bar-zone'} className={'progress-bar-zone'}>
                <Typography id={'text-upload-progress'} className={'progress-bar-title'}>Uploading {documentIndex} of {documentCount}</Typography>
                <Box id={'progress-bar-container'} className={'progress-bar-container'}>
                  <Box id={'progress-bar'} className={'progress-bar'}>
                    <LinearProgress id={'linear-progress-upload'} variant="determinate" {...props} value={progress} color='success' className={'progress'}/>
                  </Box>
                  <Typography id={'text-progress-percentage'} className={'progress-value'}>{`${progress}%`}</Typography>
                </Box>
              </Box>
            </Box>
      }
    </Box>
  );
}

export default observer(UploadComponent);
